'use client';

import React from 'react';
import { cn } from '@workspace/lib';
import { INVISIBLES } from '@workspace/constants';
import { HeroBannerBackground } from './HeroBannerBackground';
import { HeroBannerContent } from './HeroBannerContent';
import { HeroBannerSlide as HeroBannerSlideType, SanityImage } from '@workspace/interfaces';

type NormalizedHeroBannerSlide = Omit<HeroBannerSlideType, 'heading'> & {
  heading: {
    _type: string;
    fontSize?: 'h1' | 'h2' | 'h3';
    headingText: string;
  };
  videoFile?: SanityImage | null;
  poster?: SanityImage | null;
};

interface HeroBannerSlideProps {
  block: NormalizedHeroBannerSlide;
  isActive: boolean; // kept for a11y, but not used for animations
  size: 'slim' | 'medium' | 'full';
  className?: string;
  shouldPreloadImage?: boolean;
}

export function HeroBannerSlide({
  block,
  isActive,
  size,
  className,
  shouldPreloadImage = false,
}: HeroBannerSlideProps) {
  // Helpers
  const cleanContentPosition = (pos?: string): 'left' | 'center' | 'right' => {
    if (!pos || typeof pos !== 'string') return 'left';
    const cleaned = pos.replace(INVISIBLES, '').trim().toLowerCase();
    return ['left', 'center', 'right'].includes(cleaned) ? (cleaned as 'left' | 'center' | 'right') : 'left';
  };

  const getHeroBannerPaddingClass = (s: 'slim' | 'medium' | 'full'): string => {
    switch (s) {
      case 'slim':
        return 'px-md py-2xl md:px-4xl md:py-xl';
      case 'full':
        return 'px-md py-2xl md:px-4xl md:py-5xl';
      default:
        return 'px-md py-2xl md:pl-4xl md:py-4xl md:px-4xl';
    }
  };

  const positionClassMap: Record<'left' | 'center' | 'right', string> = {
    left: '',
    center: 'flex-col items-center text-center',
    right: 'flex-col items-end text-right',
  };

  if (!block) {
    return (
      <div className="embla__slide shrink-0 grow-0 basis-full relative w-full h-full overflow-hidden bg-gray-200 flex items-center justify-center">
        <div className="text-gray-500 text-center">
          <h2 className="text-2xl font-bold mb-2">Content Loading...</h2>
          <p className="text-sm">Please wait while we load the content.</p>
        </div>
      </div>
    );
  }

  const contentPosition = cleanContentPosition(block?.contentPosition);
  const paddingClass = getHeroBannerPaddingClass(size);
  const blockImage = block?.image;

  return (
    <div
      className={cn('embla__slide shrink-0 grow-0 basis-full', 'relative w-full h-full overflow-hidden')}
      role="group"
      aria-roledescription="slide"
      aria-hidden={!isActive}
      tabIndex={isActive ? 0 : -1}
    >
      {/* Static background (no parallax/transition) */}
      <div className="absolute inset-0">
        <HeroBannerBackground
          blockImage={blockImage}
          contentPosition={contentPosition}
          isVideo={block.isVideo}
          videoUrl={block.videoUrl}
          videoFile={block.videoFile}
          poster={block.poster}
          isActive={isActive}
          shouldPreload={shouldPreloadImage}
          size={size}
        />
      </div>

      {/* Static foreground content */}
      <div
        className={cn(
          paddingClass,
          positionClassMap[contentPosition],
          className,
          'relative z-1 items-center h-max porta-container',
        )}
      >
        <HeroBannerContent
          tag={block.tag}
          heading={block.heading}
          description={block.description}
          contentPosition={contentPosition}
          textColor={block.textColor}
          buttons={block.buttons}
          socialLinks={block.socialLinks}
          shareIcons={block.shareIcons}
          authorBlog={block.authorBlog}
          publicDataBlog={block.publicDataBlog}
          size={size}
        />
      </div>
    </div>
  );
}
